import { Navigate } from 'react-router-dom';
import { usePermissions } from '../contexts/PermissionsContext';
import { Loader2, Eye, ShieldX, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  demo_admin: 'Demo Admin',
  manager: 'Manager',
  operator: 'Operator',
  location_manager: 'Location Manager',
  location_user: 'Location User',
  employee: 'Employee',
};

/**
 * PermissionGuard - Wraps content that requires specific permissions
 * During preview mode, shows an inline "No Access" card instead of redirecting
 */
const PermissionGuard = ({ 
  page, 
  action = 'view', 
  actions = null, 
  requireAll = false,
  fallback = null,
  redirectTo = '/dashboard/access-denied',
  children 
}) => {
  const { can, canAny, canAll, loading, isPreviewActive, previewRole, stopPreview } = usePermissions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  let hasPermission = false;

  if (actions) {
    hasPermission = requireAll ? canAll(page, actions) : canAny(page, actions);
  } else {
    hasPermission = can(page, action);
  }

  if (!hasPermission) {
    // In preview mode: show inline "No Access" card instead of navigating away
    if (isPreviewActive) {
      return <PreviewNoAccess page={page} previewRole={previewRole} stopPreview={stopPreview} />;
    }

    if (fallback) {
      return fallback;
    }
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return null;
  }

  return children;
};

/** Inline "No Access" shown during role preview */
const PreviewNoAccess = ({ page, previewRole, stopPreview }) => {
  const roleLabel = ROLE_LABELS[previewRole] || previewRole;
  const pageLabel = page?.replace(/_/g, ' ');

  return (
    <div className="flex items-center justify-center min-h-[50vh]" data-testid="preview-no-access">
      <Card className="max-w-md w-full border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-900/40 mb-4">
            <Eye className="w-8 h-8 text-violet-600 dark:text-violet-400" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">No Access</h2>
          <p className="text-muted-foreground mb-1">
            The <strong className="text-foreground">{roleLabel}</strong> role does not have access to <strong className="capitalize text-foreground">{pageLabel}</strong>.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            You can grant access via <span className="font-medium">Role Permissions</span> settings.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button 
              variant="outline"
              onClick={() => window.history.back()}
              className="border-violet-200 dark:border-violet-700"
              data-testid="preview-go-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Go Back
            </Button>
            <Button 
              onClick={stopPreview}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="preview-exit-btn"
            >
              <ShieldX className="w-4 h-4 mr-1.5" />
              Exit Preview
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * ActionButton - Only renders if user has permission for the action
 */
export const ActionButton = ({ page, action, children, ...props }) => {
  const { can, loading } = usePermissions();

  if (loading || !can(page, action)) {
    return null;
  }

  return children;
};

/**
 * usePagePermission - Hook to check page permissions
 */
export const usePagePermission = (page) => {
  const { can, loading } = usePermissions();
  
  return {
    loading,
    canView: can(page, 'view'),
    canCreate: can(page, 'create'),
    canEdit: can(page, 'edit'),
    canDelete: can(page, 'delete'),
    canExport: can(page, 'export'),
  };
};

export default PermissionGuard;
